import { useEffect, useState } from "react";
import { AdminShell } from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Phone, Sparkle, PlayCircle, Trash, ChatCircleText, Clock, Warning, CheckCircle, ArrowRight } from "@phosphor-icons/react";

const SAMPLES = [
  { label: "🚨 Emergency leak", text: "Hi this is Sam at 1200 Sample St unit 8A — there's water pouring from my kitchen ceiling, the whole floor is soaked. Please call me back at 555 123 4567." },
  { label: "🥶 No heat", text: "This is Alex in unit 3B at 500 Main St. Furnace stopped working this morning, house is 55 degrees. My number is 5559990122." },
  { label: "🔑 Lockout", text: "Hi, I locked myself out at 1200 Sample St, unit 11C. Any chance someone can come let me in? I'll wait outside." },
  { label: "🗓️ Showing request", text: "Hi I saw your listing on Zillow for 100 Demo Street. Could I book a showing this Saturday? Call me at 555 222 1010." },
  { label: "💰 Rent question", text: "Hey it's the tenant at 100 Demo Street. Just wanted to check the balance on my account and see if you got my check last week." },
];

const urgencyColor = (u) => ({
  emergency: "bg-red-700 text-white",
  urgent: "bg-amber-500 text-white",
  routine: "bg-slate-600 text-white",
  info_only: "bg-cyan-600 text-white",
}[u] || "bg-slate-400 text-white");

export default function VoiceReception() {
  const [transcript, setTranscript] = useState("");
  const [fromNumber, setFrom] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [autoJob, setAutoJob] = useState(true);
  const [calls, setCalls] = useState([]);
  const [counts, setCounts] = useState({});

  const loadCalls = async () => {
    try {
      const r = await api.get("/voice/calls");
      setCalls(r.data.calls || []);
      setCounts(r.data.counts || {});
    } catch { /* ignore */ }
  };
  useEffect(() => { loadCalls(); }, []);

  const simulate = async () => {
    if (!transcript.trim()) return toast.error("Paste or type a transcript first");
    setBusy(true); setResult(null);
    try {
      const r = await api.post("/voice/simulate", {
        transcript, from_number: fromNumber, auto_create_job: autoJob,
      });
      setResult(r.data);
      loadCalls();
      if (r.data.job_id) toast.success("Triaged & job created");
      else toast.success("Triaged");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Failed")); }
    finally { setBusy(false); }
  };

  const createJobFor = async (id) => {
    try {
      const r = await api.post(`/voice/calls/${id}/create-job`, {});
      toast.success(`Job created (#${r.data.job_id.slice(-6)})`);
      loadCalls();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Failed")); }
  };

  const dismiss = async (id) => {
    if (!window.confirm("Dismiss this call without creating a job?")) return;
    try { await api.post(`/voice/calls/${id}/dismiss`, {}); loadCalls(); }
    catch { toast.error("Failed"); }
  };

  return (
    <AdminShell>
      <div className="p-4 md:p-6 space-y-4" data-testid="voice-reception-page">
        <div>
          <h1 className="text-4xl font-black uppercase tracking-widest flex items-center gap-2">
            <Phone weight="fill" className="text-fuchsia-500"/> AI Voice Reception
          </h1>
          <p className="text-sm text-slate-600 mt-2 max-w-3xl">
            Missed calls hit the answering line, get transcribed, and Claude triages them into structured tickets. Emergencies auto-create a job; everything else queues here for one-click approval.
          </p>
        </div>

        <div className="p-2 bg-amber-50 border-l-4 border-amber-500 text-[11px] flex items-start gap-2">
          <Warning size={14} className="text-amber-600 mt-0.5"/>
          <span><b>Twilio not yet connected.</b> Simulate calls below to preview the pipeline. Add Twilio credentials + a phone number when ready and point Twilio's Voice webhook at <code className="bg-white px-1 border">{process.env.REACT_APP_BACKEND_URL || ""}/api/voice/incoming</code> — no code change required.</span>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="voice-counts">
          <StatCard label="Triaged" value={counts.triaged || 0} bg="bg-slate-700"/>
          <StatCard label="Job Created" value={counts.job_created || 0} bg="bg-emerald-600"/>
          <StatCard label="Received" value={counts.received || 0} bg="bg-cyan-600"/>
          <StatCard label="Dismissed" value={counts.dismissed || 0} bg="bg-slate-400"/>
        </div>

        {/* Simulate Panel */}
        <div className="rugged-card p-4 space-y-3">
          <h2 className="text-sm font-black uppercase tracking-widest flex items-center gap-2 text-fuchsia-700">
            <Sparkle weight="fill"/> Simulate a Call
          </h2>
          <div className="flex flex-wrap gap-2" data-testid="voice-samples">
            {SAMPLES.map(s => (
              <button key={s.label} onClick={() => setTranscript(s.text)}
                data-testid={`sample-${s.label.replace(/[^a-z]/gi, "").toLowerCase().slice(0,6)}`}
                className="h-8 px-3 border-2 border-slate-900 bg-white hover:bg-slate-100 font-black uppercase tracking-widest text-[10px]">
                {s.label}
              </button>
            ))}
          </div>
          <input value={fromNumber} onChange={e => setFrom(e.target.value)}
            placeholder="Caller number (optional)" className="w-full h-10 px-3 border-2 border-slate-300 text-sm"/>
          <textarea value={transcript} onChange={e => setTranscript(e.target.value)} rows={5}
            placeholder="Paste (or type) a call transcript here…" data-testid="voice-transcript"
            className="w-full p-3 border-2 border-slate-300 text-sm"/>
          <div className="flex items-center gap-3 flex-wrap">
            <label className="text-xs inline-flex items-center gap-2">
              <input type="checkbox" checked={autoJob} onChange={e => setAutoJob(e.target.checked)} data-testid="auto-job-check"/>
              Auto-create job when triage recommends it
            </label>
            <button onClick={simulate} disabled={busy || !transcript.trim()} data-testid="voice-triage-btn"
              className="ml-auto h-11 px-4 bg-fuchsia-600 text-white font-black uppercase tracking-widest text-sm shadow-[3px_3px_0_#0f172a] disabled:opacity-50 inline-flex items-center gap-2">
              {busy ? "Triaging…" : <><PlayCircle size={16}/> Run Triage</>}
            </button>
          </div>
          {result && <TriageResult r={result}/>}
        </div>

        {/* Queue */}
        <div>
          <h2 className="text-lg font-black uppercase tracking-widest flex items-center gap-2 mt-4 mb-2">
            <ChatCircleText weight="fill" className="text-slate-700"/> Recent Calls
          </h2>
          {calls.length === 0 ? (
            <div className="rugged-card p-6 text-center text-sm text-slate-500">No calls yet — try a sample above.</div>
          ) : (
            <div className="space-y-2">
              {calls.map(c => <CallRow key={c.id} c={c} onCreate={() => createJobFor(c.id)} onDismiss={() => dismiss(c.id)}/>)}
            </div>
          )}
        </div>
      </div>
    </AdminShell>
  );
}

function StatCard({ label, value, bg }) {
  return (
    <div className={`p-3 ${bg} text-white shadow-[3px_3px_0_#0f172a]`}>
      <div className="text-[10px] font-black uppercase tracking-widest opacity-70">{label}</div>
      <div className="text-2xl font-black">{value}</div>
    </div>
  );
}

function TriageResult({ r }) {
  const t = r.triage || {};
  return (
    <div className="border-4 border-slate-900 bg-slate-50 p-3 space-y-2" data-testid="triage-result">
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`px-2 h-6 leading-6 font-black uppercase tracking-widest text-[9px] ${urgencyColor(t.urgency)}`}>{t.urgency}</span>
        <span className="px-2 h-6 leading-6 bg-slate-900 text-white font-black uppercase tracking-widest text-[9px]">{t.category}</span>
        <span className="px-2 h-6 leading-6 bg-fuchsia-700 text-white font-black uppercase tracking-widest text-[9px]">{t.suggested_action}</span>
        {t.recommended_tradesman_type && t.recommended_tradesman_type !== "none" && (
          <span className="px-2 h-6 leading-6 bg-cyan-600 text-white font-black uppercase tracking-widest text-[9px]">{t.recommended_tradesman_type}</span>
        )}
        {r.job_id && (
          <span className="px-2 h-6 leading-6 bg-emerald-600 text-white font-black uppercase tracking-widest text-[9px] inline-flex items-center gap-1">
            <CheckCircle size={11}/> Job #{r.job_id.slice(-6)}
          </span>
        )}
      </div>
      <div className="text-sm"><b>{t.caller_name || "Unknown caller"}</b>{t.callback_number && ` · ${t.callback_number}`}{t.caller_type && ` (${t.caller_type})`}</div>
      {(t.property_address || t.unit) && <div className="text-xs text-slate-600">{t.property_address}{t.unit && ` · Unit ${t.unit}`}</div>}
      <div className="text-sm text-slate-800">{t.summary}</div>
      {t.suggested_job_title && (
        <div className="text-[11px] flex items-center gap-1">
          <ArrowRight size={12}/> Suggested title: <b>{t.suggested_job_title}</b>
        </div>
      )}
    </div>
  );
}

function CallRow({ c, onCreate, onDismiss }) {
  const t = c.triage || {};
  const canCreate = c.status !== "job_created" && c.status !== "dismissed";
  return (
    <div className="p-3 rugged-card space-y-1" data-testid={`call-${c.id}`}>
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <Clock size={12} className="text-slate-500"/>
            <span className="text-[10px] text-slate-500 font-mono">{(c.created_at || "").slice(0, 16).replace("T", " ")}</span>
            {t.urgency && <span className={`px-2 h-5 leading-5 font-black uppercase tracking-widest text-[8px] ${urgencyColor(t.urgency)}`}>{t.urgency}</span>}
            {t.category && <span className="px-2 h-5 leading-5 bg-slate-900 text-white font-black uppercase tracking-widest text-[8px]">{t.category}</span>}
            {c.job_id && <span className="px-2 h-5 leading-5 bg-emerald-600 text-white font-black uppercase tracking-widest text-[8px]">JOB #{c.job_id.slice(-6)}</span>}
            {c.status === "dismissed" && <span className="px-2 h-5 leading-5 bg-slate-400 text-white font-black uppercase tracking-widest text-[8px]">DISMISSED</span>}
            <span className="text-[9px] text-slate-500">{c.source}</span>
          </div>
          <div className="text-sm font-black">{t.caller_name || c.from_number || "Unknown"}{t.callback_number && ` · ${t.callback_number}`}</div>
          {(t.property_address || t.unit) && <div className="text-[10px] text-slate-600">{t.property_address}{t.unit && ` · Unit ${t.unit}`}</div>}
          <div className="text-[11px] text-slate-700 line-clamp-2">{t.summary || c.transcript}</div>
        </div>
        {canCreate && (
          <div className="flex gap-1 shrink-0">
            <button onClick={onCreate} data-testid={`row-create-${c.id}`}
              className="h-8 px-2 bg-emerald-600 text-white font-black uppercase tracking-widest text-[9px] inline-flex items-center gap-1">
              <PlayCircle size={11}/> Create Job
            </button>
            <button onClick={onDismiss} className="h-8 w-8 border-2 border-slate-400 text-slate-500 hover:bg-slate-50 inline-flex items-center justify-center">
              <Trash size={12}/>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
